import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaSave, FaUndo } from 'react-icons/fa';
import { apiService } from '../services/api';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { toast } from 'react-toastify';

const AdminHomepage = () => {
  const [settings, setSettings] = useState({
    hero_title: '',
    hero_subtitle: '',
    availability_status: 'available',
    location: '',
    timezone: '',
    response_time: '',
    featured_projects: []
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await apiService.getSettings();
      setSettings(response.data || {
        hero_title: '',
        hero_subtitle: '',
        availability_status: 'available',
        location: '',
        timezone: '',
        response_time: '',
        featured_projects: []
      });
    } catch (error) {
      console.error('Error fetching homepage settings:', error);
      toast.error('Failed to fetch homepage settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await apiService.updateSettings(settings);
      toast.success('Homepage settings updated successfully');
    } catch (error) {
      toast.error('Failed to update homepage settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (window.confirm('Are you sure you want to reset changes?')) {
      fetchSettings();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white">Homepage Settings</h1>
          <p className="text-gray-400 mt-1">Manage your homepage content</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Hero Title
            </label>
            <input
              type="text"
              value={settings.hero_title || ''}
              onChange={(e) => setSettings({ ...settings, hero_title: e.target.value })}
              className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="e.g., Iliyasu Abdurrazaq"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Hero Subtitle
            </label>
            <input
              type="text"
              value={settings.hero_subtitle || ''}
              onChange={(e) => setSettings({ ...settings, hero_subtitle: e.target.value })}
              className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="e.g., Full Stack Software Engineer"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">
            Availability Status
          </label>
          <select
            value={settings.availability_status || 'available'}
            onChange={(e) => setSettings({ ...settings, availability_status: e.target.value })}
            className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="available">Available</option>
            <option value="busy">Busy</option>
            <option value="unavailable">Unavailable</option>
          </select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Location
            </label>
            <input
              type="text"
              value={settings.location || ''}
              onChange={(e) => setSettings({ ...settings, location: e.target.value })}
              className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="e.g., Kaduna, Nigeria"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Timezone
            </label>
            <input
              type="text"
              value={settings.timezone || ''}
              onChange={(e) => setSettings({ ...settings, timezone: e.target.value })}
              className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="e.g., GMT+1"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Response Time
            </label>
            <input
              type="text"
              value={settings.response_time || ''}
              onChange={(e) => setSettings({ ...settings, response_time: e.target.value })}
              className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="e.g., < 24 Hours"
            />
          </div>
        </div>

        <div className="flex gap-4 pt-4 border-t border-white/10">
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2 bg-gradient-to-r from-primary-500 to-primary-600 rounded-lg font-medium text-white hover:shadow-lg hover:shadow-primary-500/30 transition-all duration-300 flex items-center gap-2 disabled:opacity-50"
          >
            <FaSave />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
          <button
            type="button"
            onClick={handleReset}
            className="px-6 py-2 border border-white/10 rounded-lg font-medium text-gray-400 hover:bg-white/5 transition-all duration-300 flex items-center gap-2"
          >
            <FaUndo />
            Reset
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminHomepage;